import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import normalize from 'components/Dashboard/utilities/normalizeString';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import ChangeKeywordsExclusions from 'components/Dashboard/utilities/changeKeywordExclusions';
import { store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import SkeletonRelatedContent from './SkeletonRelatedContent';
import locationSearchDescriptor from 'components/Dashboard/helpers/locationSearchDescriptor';
import TotalContributionsWidget from './TotalContributionsWidget';
import ReferenceTopicWidget from 'components/Dashboard/SubNav/ReferenceTopicsWidget';
import useEditReferenceState from 'components/Dashboard/SubNav/useEditReferenceState';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import CreateReportFromUpdate from './CreateReportFromUpdate';
import EmailPerformanceWidget from './EmailPerformanceWidget';
import { createSectionName } from 'components/Dashboard/helpers/controlActiveFilters';
import idsWithoutImages from 'components/Dashboard/KeyUpdates/idsWithoutImages';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import AIassistantWidget from './AIassistantWidget';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import getAppSite, { useSiteSectionsNames } from 'utils/getAppSite';
import ConfigData from 'config/ConfigData';
import NotesWidget from 'components/ContentRelated/NotesWidget';
import { useAuth0 } from '@auth0/auth0-react';
import time from 'utils/time';
import BillTrackerWidget from './BillTrackerWidget';

const ContentRelated = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { contentResults, setContentResults } = useContentResults();
  const { search, keywordsLists, activePlan } = state;
  const { list } = search;
  const { enableAdvancedSearch } = activePlan;
  const keywordExclusions = list !== null ? list.keywordExclusions : [];
  const [isLoading, setIsLoading] = useState(false);
  const [openAIassistantChat, setOpenAIassistantChat] = useState(false);
  const [openNotesWidget, setOpenNotesWidget] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const scrollbarRef = useRef(null);
  const {
    content,
    relatedStakeholders,
    relatedStakeholdersRendered,
    mainSection,
    isLoadingRelatedContent,
    totalContributions,
    reachedTheEnd,
    referencePopupProps,
    keywordsListsIds,
    data,
  } = props;

  const { setShowPopup, keywordsSection, setKeywordsSection } = referencePopupProps ?? {};
  const {
    KeyUpdate,
    Legislation,
    Consultation,
    ParliamentaryRecord,
    StakeHolders,
    HansardContent,
    CommonsLibraryArticle,
    CountrySpecificParliamentaryRecord,
    SeneddCommitteeTranscript,
    CommitteePublication,
    RealtimeUtterance,
    CongressionalRecord,
  } = !!content && Object.keys(content)?.length > 0 ? content.hits : {};

  const [heightContainer, containerRef] = useHeightContainer(false);
  const StakeholderConditional = StakeHolders && StakeHolders.length > 0;
  const numberOfStakeHoldersToShow = parseInt(import.meta.env.VITE_APP_RELATED_STAKEHOLDERS_TO_SHOW);

  const source = new AbortController();

  const sectionName = createSectionName();
  const referencePositionContainer = useRef(null);

  const { generalApiCall } = useGeneralApiCall();
  const { getName } = useSiteSectionsNames();
  const { isAuthenticated } = useAuth0();

  useEffect(() => {
    return () => {
      source.abort('Content related canceled by the user.');
    };
  }, []);

  const moreNumber = () => {
    let numberOfResults = relatedStakeholders.length - relatedStakeholdersRendered;
    if (numberOfResults > numberOfStakeHoldersToShow) {
      return numberOfStakeHoldersToShow;
    } else {
      return numberOfResults;
    }
  };

  const loadMoreResults = async () => {
    setIsLoading(true);
    let stakeholderFilters = [];
    let contactsIdsCalled = [];
    for (let i = 0; i < numberOfStakeHoldersToShow; i++) {
      let contactId = relatedStakeholders[i];
      contactsIdsCalled.push(contactId);
      stakeholderFilters.push({ field: 'id', value: `${contactId}`, operator: 'str_eq' });
    }

    let requestProperties = {
      includeExplanations: list !== null,
      mustNotQueries: ChangeKeywordsExclusions(keywordExclusions),
      pageNumber: 1,
      pageSize: numberOfStakeHoldersToShow,
      query: '',
      type: 'stakeholder',
      filters: stakeholderFilters,
    };

    const pathname = `/api/search/query`;
    const method = 'post';
    const stakeHolderResults = await generalApiCall({ pathname, method, requestProperties, requestSource: source });

    let newResults = content['hits'].StakeHolders.concat(stakeHolderResults.hits);

    let newHits = {
      ...content['hits'],
      StakeHolders: newResults,
    };

    let contentMainSection = contentResults[mainSection];
    let newMainSection = { ...contentMainSection };
    newMainSection = {
      ...newMainSection,
      contentRelated: {
        ...newMainSection.contentRelated,
        hits: newHits,
      },
      relatedStakeholders: newMainSection?.relatedStakeholders?.filter((id) => !contactsIdsCalled.includes(id)),
      relatedStakeholdersRendered: newMainSection.relatedStakeholdersRendered + newResults.length,
    };

    setContentResults({
      ...contentResults,
      [mainSection]: newMainSection,
    });
    setIsLoading(false);
  };

  const parliamentaryRecordSections = () => {
    const sections = {
      QuestionAndAnswer: [],
      ScotlandParliamentaryRecord: [],
      WalesCountrySpecificParliamentaryContribution: [],
      ScotlandQuestionAndAnswer: [],
      WalesQuestionAndAnswer: [],
      NorthernIrelandQuestionAndAnswer: [],
      Edm: [],
    };
    if (ParliamentaryRecord) {
      for (let i in sections) {
        sections[i] = ParliamentaryRecord.filter((item) => item.contentType === i);
      }
    }
    return sections;
  };

  const {
    QuestionAndAnswer,
    ScotlandParliamentaryRecord,
    WalesCountrySpecificParliamentaryContribution,
    ScotlandQuestionAndAnswer,
    WalesQuestionAndAnswer,
    NorthernIrelandQuestionAndAnswer,
    Edm,
  } = parliamentaryRecordSections();

  const totalContributionsCondition = totalContributions?.hits;
  const { parliamentaryRecordWidgetSection } = useEditReferenceState();

  const openChatConditional = openAIassistantChat || openNotesWidget;
  const createKeywordLists = () => {
    let lists = [];
    if (keywordsListsIds?.length > 0) {
      lists = keywordsLists.filter((item) => keywordsListsIds?.includes(item.id));
    } else if (getUrlParam('topic-id')) {
      lists = keywordsLists.filter((item) => item?.id === parseInt(getUrlParam('topic-id')));
    }

    return lists;
  };

  return (
    <>
      {isLoadingRelatedContent && <SkeletonRelatedContent />}
      {!isLoadingRelatedContent && (
        <div className={`related-content ${openChatConditional ? 'related-content-open-assistant' : ''}`}>
          <div ref={containerRef} />
          <CustomScrollbar
            className={`simple-scrollbar related-content-scroll`}
            style={{ height: `${heightContainer}px` }}
            maximalThumbYSize={100}
            ref={scrollbarRef}
            onScroll={(scrollValues) => {
              if (!openAIassistantChat) {
                setScrollPosition(scrollValues?.scrollTop);
              }
            }}
          >
            <div className={`h-100 ${openChatConditional ? 'overflow-hidden' : ''}`}>
              <>
                <div ref={referencePositionContainer}>
                  {mainSection === 'Legislation' && <BillTrackerWidget legislationId={data?.id} />}
                  {data?.oneSentenceSummary && (
                    <div className='p-3 box-rounded box-outstanding-reports bg-white mb-3'>
                      <h3 className='font-weight-bold user-select-none'>At a glance</h3>
                      <p className='mb-0 related-item-text'>{data?.oneSentenceSummary}</p>
                    </div>
                  )}
                  {mainSection === 'CrmInteractions' && <EmailPerformanceWidget crmEmailId={data?.sourceCrmEmailId} />}
                  <CreateReportFromUpdate
                    show={data?.objectType !== 'RegulationDocket' && data?.objectType !== 'RegulationDocumentComment'}
                    currentNew={data}
                  />
                </div>
                {isAuthenticated && (
                  <NotesWidget
                    openNotesWidgetState={{ openNotesWidget, setOpenNotesWidget }}
                    heightContainer={heightContainer}
                    referencePositionContainer={referencePositionContainer}
                    data={data}
                    scrollbarRef={scrollbarRef}
                  />
                )}
                {mainSection !== 'CrmInteractions' && (
                  <AIassistantWidget
                    openAIassistantChatState={{ openAIassistantChat, setOpenAIassistantChat }}
                    heightContainer={heightContainer}
                    referencePositionContainer={referencePositionContainer}
                    data={data}
                    scrollbarRef={scrollbarRef}
                    scrollPosition={scrollPosition}
                  />
                )}
                {parliamentaryRecordWidgetSection &&
                  (keywordsListsIds?.length > 0 ||
                    getUrlParam('topic-id') ||
                    getUrlParam('search') ||
                    getUrlParam('or')) && (
                    <div className='box-outstanding bg-white box-rounded mb-4 parliamentary-widget position-relative z-10'>
                      {enableAdvancedSearch && (
                        <div className='p-3 border-bottom'>
                          <ReferenceTopicWidget
                            keywordsLists={createKeywordLists()}
                            setShowPopup={setShowPopup}
                            keywordsSection={keywordsSection}
                            setKeywordsSection={setKeywordsSection}
                          />
                        </div>
                      )}
                      {totalContributionsCondition && (
                        <TotalContributionsWidget reachedTheEnd={reachedTheEnd} hits={totalContributions.hits} />
                      )}
                    </div>
                  )}
              </>
              {!!content && mainSection !== 'CrmInteractions' && (
                <div className={`px-3 pt-3 pb-3 box-rounded box-outstanding bg-white related-content-main-content`}>
                  {Object.keys(content)?.length > 0 && (
                    <>
                      {relatedStakeholders !== null && StakeholderConditional && (
                        <>
                          <h3>Related stakeholders </h3>
                          <ul className='policy-list px-0'>
                            {StakeHolders.map((item) => {
                              const { objectID, title, contentUrl, imageId, party, imageUrl } = item;
                              return (
                                <Link
                                  key={objectID}
                                  to={`${contentUrl}${locationSearchDescriptor({
                                    topicId:
                                      sectionName === 'KeyUpdates'
                                        ? getUrlParam('stakeholder-topic') === 'false'
                                          ? 'all'
                                          : getUrlParam('topic-id')
                                        : null,
                                  })}`}
                                  className={`related-item`}
                                >
                                  <li className='d-flex align-items-center px-0'>
                                    <div
                                      className={`stakeholder-profile-image stakeholder-profile-related member-picture member-${
                                        party !== null ? party.toLowerCase() : ''
                                      }-picture`}
                                      style={{
                                        backgroundImage: imageUrl
                                          ? `url(${imageUrl})`
                                          : imageId !== 0 && imageId !== null && !idsWithoutImages.includes(imageId)
                                            ? `url('https://www.parliamenttoday.com/image.php?imageid=${imageId}&ml=1')`
                                            : `url('${
                                                import.meta.env.VITE_APP_CDNURL
                                              }/images/20210722-stakeholders-placeholder.png')`,
                                      }}
                                    />
                                    <p className='related-title related-stakeholder-item'>{title}</p>
                                  </li>
                                </Link>
                              );
                            })}
                          </ul>
                          {relatedStakeholders?.length > relatedStakeholdersRendered && (
                            <button
                              className='general-button see-more-link see-more-category see-more-related mb-3'
                              onClick={() => {
                                if (!isLoading) {
                                  loadMoreResults();
                                }
                              }}
                            >
                              <span className='px-2'>See {moreNumber()} more</span>
                            </button>
                          )}
                        </>
                      )}
                      <RelatedItems
                        content={KeyUpdate}
                        title={'Key updates'}
                        titleToShow={getName({ property: 'Key updates' })?.toLowerCase()}
                      />
                      <RelatedItems content={Consultation} title={'consultations'} />
                      <RelatedItems content={Legislation} title={'legislation'} />
                      <RelatedItems content={HansardContent} title={'Hansard updates'} />
                      <RelatedItems
                        content={CountrySpecificParliamentaryRecord}
                        title={'Devolved parliament official reports'}
                      />
                      <RelatedItems content={SeneddCommitteeTranscript} title={'Senedd Committee Transcripts'} />
                      <RelatedItems content={QuestionAndAnswer} title={'Q and As (Westminster)'} />
                      <RelatedItems content={ScotlandParliamentaryRecord} title={'official report (Scotland)'} />
                      <RelatedItems
                        content={WalesCountrySpecificParliamentaryContribution}
                        title={'official report (Wales)'}
                      />
                      <RelatedItems content={ScotlandQuestionAndAnswer} title={'Q and As (Scotland)'} />
                      <RelatedItems content={WalesQuestionAndAnswer} title={'Q and As (Wales)'} />
                      <RelatedItems content={NorthernIrelandQuestionAndAnswer} title={'Q and As (NI)'} />
                      <RelatedItems content={Edm} title={'EDMs'} />
                      <RelatedItems content={CommonsLibraryArticle} title={'library material'} />
                      <RelatedItems content={CommitteePublication} title={'committee publications'} />
                      <RelatedItems content={RealtimeUtterance} title={'live updates'} />
                      {getAppSite() === 'usa' && (
                        <RelatedItems content={CongressionalRecord} title={'Congressional Records'} />
                      )}
                    </>
                  )}
                  {(KeyUpdate?.length === 0 || !KeyUpdate) &&
                    (Legislation?.length === 0 || !Legislation) &&
                    (ParliamentaryRecord?.length === 0 || !ParliamentaryRecord) &&
                    (Consultation?.length === 0 || !Consultation) &&
                    (HansardContent?.length === 0 || !HansardContent) &&
                    (CommitteePublication?.length === 0 || !CommitteePublication) &&
                    (RealtimeUtterance?.length === 0 || !RealtimeUtterance) &&
                    (!StakeHolders || (StakeHolders && StakeHolders?.length === 0)) && (
                      <>
                        <h3>Related to this</h3>
                        <p className='no-related-message mb-0'>
                          <span className='icon-warning-triangle'></span>No related content found
                        </p>
                      </>
                    )}
                </div>
              )}
            </div>
          </CustomScrollbar>
        </div>
      )}
    </>
  );
};

export const relatedItemsIconMapping = {
  'key-updates': 'key-update',
  'key-update': 'key-update',
  consultations: 'consultation',
  consultation: 'consultation',
  legislation: 'legislation',
  'briefing-material': 'briefing-material',
  'policy-ask': 'policy-ask',
  'policy-asks': 'policy-ask',
  'q-and-as-': ConfigData.icons.parliament,
  'q-and-as-scotland-': ConfigData.icons.parliament,
  'q-and-as-wales-': ConfigData.icons.parliament,
  'q-and-as-ni-': ConfigData.icons.parliament,
  'official-report-scotland-': ConfigData.icons.parliament,
  'official-report-wales-': ConfigData.icons.parliament,
  'q-and-as-westminster-': ConfigData.icons.parliament,
  'hansard-updates': ConfigData.icons.parliament,
  'parliamentary-record': ConfigData.icons.parliament,
  'congressional-record': ConfigData.icons.parliament,
  'devolved-parliament-official-reports': ConfigData.icons.parliament,
  'senedd-committee-transcripts': ConfigData.icons.parliament,
  edms: ConfigData.icons.parliament,
  'library-material': 'library-material',
  'congressional-records': ConfigData.icons.parliament,
  'committee-publications': 'document',
  'live-updates': 'play',
};

const RelatedItems = (props) => {
  const globalState = useContext(store);
  const { dispatch } = globalState;
  const { content, titleToShow, title } = props;
  return (
    <>
      {content?.length > 0 && (
        <>
          <h3>Related {titleToShow ?? title}</h3>
          <ul className='policy-list px-0'>
            {content.map((item) => {
              const { objectID, title, contentUrl, dateTime } = item;
              return (
                <Link
                  key={objectID}
                  to={`${contentUrl}${locationSearchDescriptor()}`}
                  className={`related-item related-${normalize(props.title)}`}
                  onClick={() => {
                    dispatch({
                      type: 'MODIFY_SECTION',
                      parameter: 'clickedOnContentUrl',
                      value: null,
                    });
                  }}
                >
                  <span className={`icon-${relatedItemsIconMapping[normalize(props.title)]}`}></span>
                  <li>
                    <p className='related-title'>{title}</p>
                    <p className='related-date'>{time(dateTime).formatGeneralDate()}</p>
                  </li>
                </Link>
              );
            })}
          </ul>
        </>
      )}
    </>
  );
};

export default ContentRelated;
